import { ReactNode, useRef } from 'react';

import { PageHeaderContext, PageHeaderContextValue, PageHeaderControl } from 'components/layout/page/header/context';
import { PageHeader, type PageHeaderProps } from 'components/layout/page/header/PageHeader';
import { useSuperState } from 'hooks';

export type PageWithHeaderProps = {
    children: ReactNode;
    headerConfig: PageHeaderProps;
};

/* Provides an interface for nested components to dynamically modify header functionality (primary/secondary CTAs, table exports) */
export const PageWithHeader = (props: PageWithHeaderProps) => {

    const { children, headerConfig } = props;

    const [state, setState] = useSuperState({
        activeId: null as symbol | null,
        contribution: null as PageHeaderControl | null,

        // Bumped on every (re-)register so the header re-renders and re-reads the registered contribution's live getter values, even when the registered object reference is unchanged.
        nonce: 0
    });

    // useSuperState's setter is not referentially stable, so keep the latest setter in a ref and expose a single, stable context value.
    // A stable provider value prevents descendant registration effects from re-running (and looping) on every parent render.
    const setStateRef = useRef(setState);
    setStateRef.current = setState;

    const nonceRef = useRef(0);

    const apiRef = useRef<PageHeaderContextValue | null>(null);
    if (apiRef.current === null) {

        apiRef.current = {
            register: (id: symbol, contribution: PageHeaderControl) => {

                nonceRef.current += 1;
                setStateRef.current({ activeId: id, contribution, nonce: nonceRef.current });

            },

            // Only clear the slot if the id being unregistered is the one currently occupying it.
            // This keeps tab switches (where one contributor registers as another unregisters) from wiping a freshly-set contribution.
            unregister: (id: symbol) => setStateRef.current(prev => (prev.activeId === id ? { activeId: null, contribution: null } : prev))
        };

    }

    const registered = state.contribution;
    const mergedActions = [...(headerConfig.actions ?? []), ...(registered?.actions ?? [])];

    return (
        <PageHeaderContext.Provider value={apiRef.current}>
            <PageHeader
                {...headerConfig}
                actions={mergedActions.length ? mergedActions : undefined}
                cta={registered?.cta ?? headerConfig.cta}
                exportHandle={registered?.exportHandle ?? null}
            />
            {children}
        </PageHeaderContext.Provider>
    );

};
